import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ErrorHelper } from '../../../shared/helpers/error.helper';
import {
  MessageHelper,
  MessageTypeEnum,
} from '../../../shared/helpers/message.helper';
import { AuthService } from '../../../shared/services/auth.service';
import { SidenavComponent } from '../../sidenav.component';
import { AuthStateService } from '../../services/auth-state.service';

@Component({
  selector: 'app-login',
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private unsub = new Subject();
  private loading = false;
  form?: FormGroup;

  @Input() sidenav?: SidenavComponent;

  constructor(
    private fb: FormBuilder,
    private service: AuthService,
    private router: Router,
    private messageHelper: MessageHelper,
    private errorHelper: ErrorHelper,
    private authState: AuthStateService
  ) {}

  ngOnInit(): void {
    this.buildForm();

    this.sidenav?.controller?.openedChange
      .pipe(takeUntil(this.unsub))
      .subscribe((opened) => {
        if (!opened) {
          this.form?.reset();
        }
      });
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }

  buildForm() {
    this.form = this.fb.group({
      user: [null, Validators.required],
      password: [null, Validators.required],
    });
  }

  async login() {
    if (this.loading) return;

    if (this.form?.invalid) {
      this.messageHelper.send(
        'Username And/Or password are required',
        MessageTypeEnum.error
      );
      return;
    }

    try {
      this.loading = true;
      const { user, password } = this.form?.value;
      const { status } = await this.service.login(user, password);

      if (status && (status === 200 || status === 204)) {
        this.authState.setCredentials(user, password);

        await this.sidenav?.controller?.close();
        this.sidenav?.openSidenav('mfa');
      }
    } catch (error) {
      this.errorHelper.catch(error, {
        401: 'Username and/or password are invalid',
      });
    } finally {
      this.loading = false;
    }
  }

  async forgotPassword() {
    if (this.loading) return;

    if (this.form?.controls.user.invalid) {
      this.messageHelper.send('Username is required', MessageTypeEnum.error);
      return;
    }

    try {
      this.loading = true;
      const { user } = this.form?.value;
      await this.service.forgotPassword(user);
      this.messageHelper.send(
        'An e-mail was sent to you with a temporary password'
      );
    } catch (err) {
      this.errorHelper.catch(err, {
        401: 'Username not found, check and try again',
      });
    } finally {
      this.loading = false;
    }
  }

  async registerNewAccount() {
    await this.sidenav?.controller?.close();
    this.sidenav?.openSidenav('register');
  }

  showFieldError(field: string) {
    const formField = this.form?.get(field);
    return formField?.invalid && (formField.dirty || formField.touched);
  }
}
